import { Link } from "react-router-dom";
import { Phone, Mail, FileText, Calculator, Inbox, Sparkles, ChevronRight, FileSignature, ArrowUpRight } from "lucide-react";
import { fmtDate, initials, nok, num } from "@/lib/format";
import { label } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useAssistantOptional } from "@/context/AssistantContext";

const RailSection = ({ title, children, testId, action }) => (
  <section className="py-4 first:pt-0 last:pb-0" data-testid={testId}>
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-[11.5px] font-medium text-muted-foreground">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Person = ({ name, role, phone, email, tone, testId }) => (
  <li className="flex items-center gap-2.5" data-testid={testId}>
    <Avatar className="h-7 w-7"><AvatarFallback className={cn("text-[10px] font-semibold", tone === "navy" ? "bg-navy text-white" : "bg-muted text-foreground/80")}>{initials(name)}</AvatarFallback></Avatar>
    <div className="min-w-0 flex-1 leading-tight">
      <div className="truncate text-[13px] font-medium">{name}</div>
      <div className="truncate text-[11.5px] text-muted-foreground">{role}</div>
    </div>
    <div className="flex shrink-0 items-center gap-1">
      {phone && <a href={`tel:${phone}`} className="rounded p-1 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground" aria-label={`Ring ${name}`}><Phone className="h-3.5 w-3.5" /></a>}
      {email && <a href={`mailto:${email}`} className="rounded p-1 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground" aria-label={`Send e-post til ${name}`}><Mail className="h-3.5 w-3.5" /></a>}
    </div>
  </li>
);

/**
 * Right rail – one quiet container with hairline separators. Sections are omitted when empty (no dead cards).
 */
export const ProjectRail = ({ p, lens, can, onGo, className }) => {
  const assistant = useAssistantOptional();
  const canAsk = Boolean(assistant) && can("ai:read");
  const hasLinks = p.lead || p.quote || p.calculation || p.contract;
  const hasPeople = p.contact?.name || p.manager_name || p.operator?.name;

  return (
    <aside className={cn("divide-y divide-border/60 xl:sticky xl:top-5", className)} data-testid="project-rail">
      {hasPeople && (
        <RailSection title="Personer" testId="project-rail-people">
          <ul className="space-y-2.5">
            {p.manager_name && <Person name={p.manager_name} role="Prosjektleder · Stord" tone="navy" testId="project-rail-person-manager" />}
            {p.contact?.name && <Person name={p.contact.name} role={`${p.contact.title ? `${p.contact.title} · ` : ""}${p.customer_name}`} phone={p.contact.phone} email={p.contact.email} testId="project-rail-person-contact" />}
            {p.operator?.name && p.operator.kind !== "stord" && <Person name={p.operator.name} role="Drifter campen" testId="project-rail-person-operator" />}
          </ul>
        </RailSection>
      )}

      {hasLinks && (
        <RailSection title="Koblinger" testId="project-rail-links">
          <ul className="space-y-1.5 text-[13px]">
            {p.lead && <li><Link to={`/henvendelser/${p.lead.id}`} className="flex min-w-0 items-center gap-2 rounded-md py-0.5 hover:underline underline-offset-[3px]" data-testid="project-rail-link-lead"><Inbox className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">{p.lead.lead_number}</span><span className="min-w-0 truncate">{p.lead.title}</span></Link></li>}
            {p.quote && <li><Link to={`/tilbud/${p.quote.id}`} className="flex min-w-0 items-center gap-2 rounded-md py-0.5 hover:underline underline-offset-[3px]" data-testid="project-rail-link-quote"><FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">{p.quote.quote_number}</span><span className="min-w-0 truncate"><StatusBadge status={p.quote.status} entity="quote" /></span><span className="ml-auto shrink-0 font-mono text-[12px] tabular">{nok(p.quote.total)}</span></Link></li>}
            {p.contract && <li><Link to={`/okonomi?tab=kontrakter&id=${p.contract.id}`} className="flex min-w-0 items-center gap-2 rounded-md py-0.5 hover:underline underline-offset-[3px]" data-testid="project-rail-link-contract"><FileSignature className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">{p.contract.contract_number}</span><span className="min-w-0 truncate"><StatusBadge status={p.contract.status} entity="contract" /></span>{p.contract.total_value > 0 && <span className="ml-auto shrink-0 font-mono text-[12px] tabular">{nok(p.contract.total_value)}</span>}</Link></li>}
            {p.calculation && <li><button type="button" onClick={() => onGo({ tab: "okonomi" })} className="flex w-full items-center gap-2 rounded-md py-0.5 text-left hover:underline underline-offset-[3px]" data-testid="project-rail-link-calc"><Calculator className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-[11.5px] text-muted-foreground">{p.calculation.calc_number}</span><span>Kalkyle</span>{p.calculation.totals?.margin_pct != null && <span className="ml-auto font-mono text-[12px] tabular text-muted-foreground">DG {num(p.calculation.totals.margin_pct, 1)} %</span>}</button></li>}
          </ul>
        </RailSection>
      )}

      {lens.events.length > 0 && (
        <RailSection title="Neste" testId="project-rail-next">
          <ul className="space-y-1">
            {lens.events.map((e) => (
              <li key={e.key}>
                <button type="button" onClick={() => onGo(e)} className="group flex w-full items-baseline gap-3 rounded-md px-1 py-1 text-left -mx-1 transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid={`project-rail-event-${e.key}`}>
                  <span className="w-[60px] shrink-0 whitespace-nowrap font-mono text-[11.5px] tabular text-muted-foreground">{fmtDate(e.date, "d. MMM")}</span>
                  <span className="min-w-0 flex-1"><span className="block truncate text-[13px]">{e.text}</span>{e.sub && <span className="block truncate text-[11.5px] text-muted-foreground">{e.sub}</span>}</span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 self-center text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                </button>
              </li>
            ))}
          </ul>
        </RailSection>
      )}

      {p.suppliers?.length > 0 && (
        <RailSection title="Leverandører" testId="project-rail-suppliers">
          <ul className="space-y-1.5 text-[13px]">
            {p.suppliers.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2">
                <Link to={`/leverandorer/${s.id}`} className="min-w-0 truncate hover:underline underline-offset-[3px]">{s.name} <span className="text-[11.5px] text-muted-foreground">· {label(s.category)}</span></Link>
                {s.phone && <a href={`tel:${s.phone}`} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={`Ring ${s.name}`}><Phone className="h-3.5 w-3.5" /></a>}
              </li>
            ))}
          </ul>
        </RailSection>
      )}

      {lens.observations.length > 0 && (
        <RailSection title="Observasjoner" testId="project-rail-assistant">
          <ul className="space-y-3">
            {lens.observations.map((o) => (
              <li key={o.key} className="flex gap-2" data-testid={`project-rail-observation-${o.key}`}>
                <Sparkles className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-[12.5px] leading-5 text-foreground/85">{o.text}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[12px]">
                    {(o.action || o.tab || o.link) && <button type="button" onClick={() => onGo(o)} className="inline-flex items-center gap-1 text-foreground/80 underline-offset-[3px] hover:underline" data-testid={`project-rail-observation-${o.key}-open`}>Åpne <ArrowUpRight className="h-3 w-3" /></button>}
                    {canAsk && o.ask && <button type="button" onClick={() => assistant.ask(o.ask, { project_id: p.id })} className="text-primary underline-offset-[3px] hover:underline" data-testid={`project-rail-observation-${o.key}-ask`}>Spør assistenten</button>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </RailSection>
      )}
    </aside>
  );
};
